import React, { useMemo, useState } from 'react';
import Spinner from '../../../components/common/spinner';
import Modal from '../../../components/common/modal';
import { Paginator } from '../../../components/common/paginator';
import { convertObjectToParams } from '../../../components/common/create-params';
import TagSelectorModal from '../../../components/content-library/tag-selector-modal';
import { Tag } from '../../../hooks/useTags';
import { ListenerAudio, useUpdateListenerAudio } from '../../../hooks/useListenerAudio';
import { ROUTE_PATH } from '../../../routes/paths';
import { getListenerAudioTagNames } from '../utils/tag-helpers';
import { EmptyState, TagList, Truncate } from './common';

export interface VideoContentTableProps {
  rows: ListenerAudio[];
  tags: Tag[];
  tagMap: Map<number, Tag>;
  isLoading: boolean;
  count: number;
  limit: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onShowDetails: (experience: ListenerAudio) => void;
}

export const VideoContentTable: React.FC<VideoContentTableProps> = ({
  rows,
  tags,
  tagMap,
  isLoading,
  count,
  limit,
  currentPage,
  onPageChange,
  onShowDetails,
}) => {
  if (isLoading) {
    return <Spinner />;
  }

  if (!rows.length) {
    return <EmptyState message="No video content found." />;
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse">
          <thead>
            <tr className="bg-gray-100 text-left text-xs uppercase text-gray-500">
              <th className="w-48 px-4 py-4">Title</th>
              <th className="w-20 px-4 py-4">Photo</th>
              <th className="w-36 px-4 py-4">Listener</th>
              <th className="w-28 px-4 py-4">Client</th>
              <th className="w-28 px-4 py-4">Domain</th>
              <th className="w-20 px-4 py-4">Views</th>
              <th className="w-28 px-4 py-4">Transcription</th>
              <th className="w-44 px-4 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((experience) => (
              <VideoContentRow
                key={experience.id}
                experience={experience}
                tags={tags}
                tagMap={tagMap}
                onShowDetails={onShowDetails}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-end">
        <Paginator count={count} limit={limit} currentPage={currentPage} handlePageChange={onPageChange} />
      </div>
    </div>
  );
};

interface VideoContentRowProps {
  experience: ListenerAudio;
  tags: Tag[];
  tagMap: Map<number, Tag>;
  onShowDetails: (experience: ListenerAudio) => void;
}

const VideoContentRow: React.FC<VideoContentRowProps> = ({ experience, tags, tagMap, onShowDetails }) => {
  const [showTagsModal, setShowTagsModal] = useState(false);
  const { mutateAsync: updateTags, isLoading } = useUpdateListenerAudio();
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);

  const listenerUserId = experience.listener_role?.user?.id;
  const listenerProfileHref =
    experience.listener_role_id && listenerUserId
      ? ROUTE_PATH.LISTENERS_PROFILE +
        convertObjectToParams({
          listenerRoleId: experience.listener_role_id,
          listenerId: listenerUserId,
        })
      : null;

  const listenerName =
    experience.listener_role?.user?.display_name || experience.listener_role?.user?.first_name || 'Unknown';

  const tagNames = useMemo(() => getListenerAudioTagNames(experience, tagMap), [experience, tagMap]);

  const requiredTagIds = useMemo(() => {
    return tags.filter((tag) => tag.is_required && experience.topic_tag_ids?.includes(tag.id)).map((tag) => tag.id);
  }, [experience.topic_tag_ids, tags]);

  const handleSaveTags = async (selectedTagIds: number[]) => {
    const uniqueTagIds = Array.from(new Set(selectedTagIds));
    try {
      await updateTags({
        listenerId: experience.listener_role_id,
        audioId: experience.id,
        payload: {
          topic_tag_ids: uniqueTagIds,
        },
      });
      setShowTagsModal(false);
    } catch {
      // handled by mutation
    }
  };

  return (
    <>
      <tr className="text-gray-dark text-sm">
        <td className="px-4 py-4 align-top">
          <Truncate title={experience.ai_title || 'No AI Title Found'} className="font-semibold">
            {experience.ai_title || 'No AI Title Found'}
          </Truncate>
          <div className="text-xs text-gray-500">
            {experience.video_id ? (
              <a
                href={`https://vimeo.com/${experience.video_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-primary"
              >
                Watch Video
              </a>
            ) : (
              'No video link'
            )}
          </div>
        </td>
        <td className="px-4 py-4">
          {experience.profile_photo_square_file_url ? (
            <img
              src={experience.profile_photo_square_file_url}
              alt={listenerName}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-500">
              {listenerName.charAt(0).toUpperCase()}
            </div>
          )}
        </td>
        <td className="px-4 py-4 align-top">
          {listenerProfileHref ? (
            <a
              className="text-blue-primary block min-w-0 truncate font-semibold hover:underline"
              href={listenerProfileHref}
              target="_blank"
              rel="noopener noreferrer"
              title={listenerName}
            >
              {listenerName}
            </a>
          ) : (
            <Truncate title={listenerName}>{listenerName}</Truncate>
          )}
          {experience.listener_role_id && (
            <div className="text-xs text-gray-500">Role ID: {experience.listener_role_id}</div>
          )}
        </td>
        <td className="px-4 py-4 text-sm">
          <Truncate title={experience.listener_role?.client_name ?? undefined}>
            {experience.listener_role?.client_name ?? '—'}
          </Truncate>
        </td>
        <td className="px-4 py-4 text-sm">
          <Truncate title={experience.listener_role?.domain?.name ?? undefined}>
            {experience.listener_role?.domain?.name ?? '—'}
          </Truncate>
        </td>
        <td className="px-4 py-4 font-semibold">{experience.total_playbacks ?? 0}</td>
        <td className="px-4 py-4">
          <div className="flex justify-center">
            <button
              className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => setShowTranscriptionModal(true)}
              disabled={!experience.transcription}
            >
              View Transcription
            </button>
          </div>
        </td>
        <td className="px-4 py-4">
          <div className="flex items-center justify-center gap-2">
            <button
              className="bg-blue-primary rounded-md px-3 py-1 text-xs font-semibold text-white"
              onClick={() => setShowTagsModal(true)}
              disabled={isLoading}
            >
              Manage Tags
            </button>
            <button
              className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700"
              onClick={() => onShowDetails(experience)}
            >
              View Details
            </button>
          </div>
        </td>
      </tr>
      <tr>
        <td colSpan={8} className="border-b-2 border-gray-200 px-4 py-3">
          <TagList tags={tagNames} />
        </td>
      </tr>
      {showTagsModal && (
        <TagSelectorModal
          title="Select Tags"
          tags={tags}
          selectedTagIds={experience.topic_tag_ids}
          lockedTagIds={requiredTagIds}
          maxSelectable={5}
          onSave={handleSaveTags}
          onClose={() => setShowTagsModal(false)}
        />
      )}
      <Modal
        isModel={showTranscriptionModal}
        InnerComponent={
          <div
            className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:align-middle"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <h3 className="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                Transcription
              </h3>
              <div className="mt-3 max-h-96 overflow-y-auto whitespace-pre-wrap text-sm text-gray-700">
                {experience.transcription || 'No transcription available.'}
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="button"
                className="bg-blue-primary inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => setShowTranscriptionModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        }
      />
    </>
  );
};
